'use client'

import { useLaunchDarkly } from '@/providers/LaunchDarkly'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'
import { areClientFeatureFlagContextsEqual } from '@/utilities/client/clientPortalContext'
import { schemas, type FileAccessFlagValueType } from '@/types/portal-api.generated'

export const CLIENT_FILES_FLAG = 'client-portal-files'

export const FileAccessFlagValue = schemas.FileAccessFlagValue.enum

export type ClientFilesFlagValue = FileAccessFlagValueType

export function parseClientFilesFlag(value: unknown): ClientFilesFlagValue | null {
  const result = schemas.FileAccessFlagValue.safeParse(value)
  return result.success ? result.data : null
}

/**
 * Gives the Files access for the partner. `access` is null while the flags for that
 * partner are not ready. The nav and the Files page must both use this hook so that
 * they show the same decision.
 */
export function useClientFilesAccess(partnerId: string | null | undefined): {
  access: ClientFilesFlagValue | null
  isAccessReady: boolean
  canViewFiles: boolean
  canWriteFiles: boolean
} {
  const { flags, isLoading, hasFetched, error, clientContext } = useLaunchDarkly()
  const normalizedPartnerId = normalizeNullableClientId(partnerId)
  const isPartnerAligned = normalizedPartnerId
    ? areClientFeatureFlagContextsEqual(clientContext, {
        kind: 'partner',
        partnerId: normalizedPartnerId,
      })
    : false
  const areFlagsSettled = isPartnerAligned && !isLoading && (hasFetched || error !== null)
  const parsedValue = parseClientFilesFlag(flags[CLIENT_FILES_FLAG])

  // A flag value that is missing or not known turns the feature off.
  const access: ClientFilesFlagValue | null = areFlagsSettled
    ? (parsedValue ?? FileAccessFlagValue.disabled)
    : null

  return {
    access,
    isAccessReady: access !== null,
    canViewFiles: access === FileAccessFlagValue.read || access === FileAccessFlagValue.write,
    canWriteFiles: access === FileAccessFlagValue.write,
  }
}
